import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { TokenApiService } from 'src/app/core/services/token-api.service';
import { ToastService } from 'src/app/core/services/toast.service';
import {
  ALL_SCOPE_FEATURES, DeveloperApiToken, PermissionLevel, ScopeFeature, TokenScope,
} from 'src/app/core/domain/developer-token.model';
import { IonicModule } from '@ionic/angular';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-tokens',
    templateUrl: './tokens.page.html',
    styleUrls: ['./tokens.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        FormsModule,
        RouterLink,
        DatePipe,
        TranslatePipe,
    ],
})
export class TokensPage implements OnInit {
  readonly features = ALL_SCOPE_FEATURES;

  tokens: DeveloperApiToken[] = [];
  isLoading = true;
  loadError = false;

  showCreateForm = false;
  isSaving = false;
  newName = '';
  newExpiresInDays: number | null = 365;
  /** Draft scope levels for the create form, one entry per feature, defaults to None. */
  draftScopes: Record<ScopeFeature, PermissionLevel> = this.emptyDraft();

  /** Set only right after a successful create — the one-time raw-token reveal. */
  justCreatedToken: DeveloperApiToken | null = null;

  constructor(
    private api: TokenApiService,
    private toast: ToastService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.loadError = false;
    this.api.list().subscribe({
      next: tokens => {
        this.tokens = tokens;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.loadError = true;
        this.cdr.markForCheck();
      },
    });
  }

  openCreateForm(): void {
    this.showCreateForm = true;
    this.newName = '';
    this.newExpiresInDays = 365;
    this.draftScopes = this.emptyDraft();
    this.justCreatedToken = null;
    this.cdr.markForCheck();
  }

  cancelCreate(): void {
    this.showCreateForm = false;
    this.cdr.markForCheck();
  }

  cycleLevel(feature: ScopeFeature): void {
    const order: PermissionLevel[] = ['None', 'Read', 'Write'];
    const next = order[(order.indexOf(this.draftScopes[feature]) + 1) % order.length];
    this.draftScopes = { ...this.draftScopes, [feature]: next };
  }

  levelColor(level: PermissionLevel): string {
    switch (level) {
      case 'Write': return 'success';
      case 'Read': return 'primary';
      default: return 'medium';
    }
  }

  create(): void {
    const scopes: TokenScope[] = this.features
      .map(feature => ({ feature, level: this.draftScopes[feature] }))
      .filter(s => s.level !== 'None');

    if (!this.newName.trim()) {
      this.toast.error(this.translate.instant('tokens.errors.nameRequired'));
      return;
    }
    if (scopes.length === 0) {
      this.toast.error(this.translate.instant('tokens.errors.scopeRequired'));
      return;
    }

    this.isSaving = true;
    this.api.create({
      name: this.newName.trim(),
      scopes,
      expiresInDays: this.newExpiresInDays ?? undefined,
    }).subscribe({
      next: token => {
        this.isSaving = false;
        this.showCreateForm = false;
        this.justCreatedToken = token;
        this.load();
        this.cdr.markForCheck();
      },
      error: (err: { error?: { message?: string } }) => {
        this.isSaving = false;
        this.toast.error(err.error?.message ?? this.translate.instant('tokens.errors.createFailed'));
        this.cdr.markForCheck();
      },
    });
  }

  dismissReveal(): void {
    this.justCreatedToken = null;
    this.cdr.markForCheck();
  }

  async copyToken(token: string): Promise<void> {
    await navigator.clipboard.writeText(token);
    this.toast.success(this.translate.instant('common.copiedToClipboard'));
  }

  revoke(token: DeveloperApiToken): void {
    if (!confirm(this.translate.instant('tokens.confirmRevoke', { name: token.name }))) return;

    this.api.revoke(token.id).subscribe({
      next: () => { this.toast.success(this.translate.instant('tokens.revokedToast')); this.load(); },
      error: () => this.toast.error(this.translate.instant('tokens.errors.revokeFailed')),
    });
  }

  isRevoked(t: DeveloperApiToken): boolean { return t.revokedAt !== null; }
  isExpired(t: DeveloperApiToken): boolean { return !!t.expiresAt && new Date(t.expiresAt) < new Date(); }

  private emptyDraft(): Record<ScopeFeature, PermissionLevel> {
    return Object.fromEntries(this.features.map(f => [f, 'None' as PermissionLevel])) as Record<ScopeFeature, PermissionLevel>;
  }
}
